import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Client } from '../client';
import { Contact } from '../contact';
import { Project } from '../project';
import { Proposal } from '../proposal';
import { ProposalService } from '../proposal.service';
import { Feature } from '../feature';
import { ProjectFile } from '../project-file';
import { NgbDateParserFormatter, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';
import { faPrint, faCheck, faCircleNotch, faSpinner, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { AcceptProposalTerm } from '../accept-proposal-term';
import { Site } from '../site';
import { catchError, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Address } from 'cluster';
import { ProposalPricing } from '../proposal-pricing';
import { AcceptedProposal } from '../accepted-proposal';
import { SettingService } from '../setting.service';
import { FeatureItem } from '../feature-item';
import { AlertService } from '../alert.service';
import { MediaLightboxComponent } from '../media-lightbox/media-lightbox.component';

import { FormatDateTimePipe } from '../format-date-time.pipe';
import { SignatureCanvasComponent } from '../signature-canvas/signature-canvas.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor, NgTemplateOutlet, CurrencyPipe, DatePipe } from '@angular/common';
import { LocalDarkModeController } from '@main/local-dark-mode.service';

@Component({
    selector: 'app-proposal-view',
    templateUrl: './proposal-view.component.html',
    styleUrls: ['./proposal-view.component.css'],
    imports: [NgIf, FaIconComponent, NgFor, NgTemplateOutlet, ReactiveFormsModule, SignatureCanvasComponent, CurrencyPipe, DatePipe, FormatDateTimePipe]
})
export class ProposalViewComponent implements OnInit {

  // Preferences
  preferredInstallation: boolean = false;
  preferredInstallationOptions: { date: string, label: string }[] = [];
  preferredRemoval: boolean = false;

  // Modals
  private modalReference: NgbModalRef;
  @ViewChild('FOLLOW_UP_QUESTIONS_MODAL') private followUpQuestionsModal: NgbModal;

  // Properties
  proposalLoaded: boolean = false;
  logoUrl: string;
  proposalId: string;
  proposal: Proposal;
  taxMultiplier: number;
  proposalPricing: ProposalPricing;
  project: Project;
  projectFiles: ProjectFile[] = [];
  site: Site;
  client: Client;
  primaryContact: Contact;
  features: Feature[] = [];
  featureItems: Map<string, FeatureItem[]> = new Map<string, FeatureItem[]>();
  installationWeeks: { date: string, label: string }[] = [];
  acceptedProposal: AcceptedProposal;
  acceptProposalTerms: AcceptProposalTerm[] = [];
  businessAttributes: { name: string, email: string, phone: string, address: Address, displayAddress: boolean };
  allTermsAccepted: boolean = false;
  showTinselPayCardPassFee: boolean = false;
  followUpQuestionsForm: UntypedFormGroup;
  featureHasAtLeastOneImageMap: Map<string, boolean> = new Map<string, boolean>();
  signaturesAreEnabled: boolean = false;
  proposalIsExpired: boolean = false;
  normalizedTermsHtml = '';

  localDarkModeController: LocalDarkModeController;

  loading: boolean = false;

  // Multi Options
  multiOptionKeys: any[] = [];
  selectedMultiOption: string;

  // Font Awesome Properties
  faPrint = faPrint;
  faCheck = faCheck;
  faCircleNotch = faCircleNotch;
  faSpinner = faSpinner;
  faMoon = faMoon;
  faSun = faSun;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private proposalService: ProposalService,
    private modalService: NgbModal,
    private settingService: SettingService,
    private alertService: AlertService,
    public dateParserFormatter: NgbDateParserFormatter) { }

  ngOnInit(): void {
    this.localDarkModeController = new LocalDarkModeController('ProposalView');
    this.localDarkModeController.darkModeChanged$.subscribe((isDarkModeEnabled) => {
      if (isDarkModeEnabled) {
        document.documentElement.setAttribute('data-bs-theme', 'dark');
      } else {
        document.documentElement.setAttribute('data-bs-theme', 'light');
      }
    });
    this.proposalId = this.route.snapshot.paramMap.get('proposalId');
    this.followUpQuestionsForm = new UntypedFormGroup({
      installationWeek: new UntypedFormControl('NONE'),
      removalType: new UntypedFormControl('NONE'),
      additionalInfo: new UntypedFormControl(),
      signedBy: new UntypedFormControl()
    });
    // this.generateInstallationWeeks();
    this.printProposal();
    
  }

  // Open Modal
  openModal(content: any): void {
    this.modalReference = this.modalService.open(content);
  }

  // Open Media Lightbox
  openMediaLightbox(featureItem: FeatureItem): void {
    if (featureItem.imageType == 'NONE' || !featureItem.itemImageExists) return;
    const images = this.featureItems.get(featureItem.featureId).filter((featureItem) => { return featureItem.imageType == 'ITEM' && featureItem.itemImageExists; }).map((featureItem) => { return { id: featureItem.id, type: 'image', name: featureItem.name, url: featureItem.imageUrl }; });
    this.modalReference = this.modalService.open(MediaLightboxComponent, { centered: true, modalDialogClass: 'custom-modal-dialog', size: 'xl' });
    this.modalReference.componentInstance.lightboxItems = images;
    this.modalReference.componentInstance.selectedLightboxItemId = featureItem.id;
  }

  // Generate Installation Weeks
  private generateInstallationWeeks(): void {
    const startDate = dayjs(`${dayjs().year()}-09-01`).startOf('week');
    for (var i = 0; i < 17; i++) {
      const date = dayjs(startDate).add(i, 'week');
      this.installationWeeks.push({
        date: date.format('YYYY-MM-DD'),
        label: `Week of ${date.format('MMMM D, YYYY')}`
      });
    }
  }

  // Print Proposal
  private printProposal(): void {
    this.featureItems.clear();
    this.proposalService.printProposal(this.proposalId).pipe(catchError((error: any) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) this.router.navigateByUrl('/404', { replaceUrl: true });
      }
      return throwError(() => new Error('API Request Error'));
    })).subscribe((res) => {
      if (!this.hasBeenViewed() && !this.proposalLoaded) this.addProposalInteraction('VIEWED');
      this.proposal = res.proposal;

      this.proposalIsExpired = dayjs().unix() > dayjs(this.proposal.expirationDate).endOf('day').unix();
      // Multi Options
      if (this.proposal.agreementType == 'MULTI' && this.proposal.multiOptions) {
        console.log(this.proposal.multiOptions);
        const filtered = Object.fromEntries(
          Object.entries(this.proposal?.multiOptions).filter(([key, value]: any[]) => value.isEnabled === true)
        );
        this.multiOptionKeys = Object.keys(filtered);
        console.log(this.multiOptionKeys)
        this.selectedMultiOption = this.multiOptionKeys && this.multiOptionKeys.length > 1 ? null:this.multiOptionKeys[0];
        console.log(this.selectedMultiOption)
      }
      this.project = res.project;
      this.site = this.project.site;
      this.client = res.client;
      this.primaryContact = res.primaryContact;
      this.features =  res.features;
      const featureItems = (res.featureItems as FeatureItem[]).filter((featureItem) => { return !featureItem.hidden; });
      for (const featureItem of featureItems) {
        if (this.featureItems.has(featureItem.featureId)) {
          const tempArray = this.featureItems.get(featureItem.featureId);
          tempArray.push(featureItem);
          this.featureItems.set(featureItem.featureId, tempArray);
        } else {
          this.featureItems.set(featureItem.featureId, [featureItem]);
        }
      }
      this.taxMultiplier = parseFloat(res.taxMultiplier);
      this.projectFiles = res.projectFiles;
      this.acceptedProposal = 
      res.acceptedProposal ? 
      {
        ...res.acceptedProposal,
        dateSigned: dayjs.unix(res.acceptedProposal?.createdAt).format()
      } : null;

      if (this.acceptedProposal && this.acceptedProposal.agreementType == 'MULTI') this.selectedMultiOption = this.acceptedProposal.selectedMultiOption;
      this.logoUrl = (res.organizationLogoExists) ? this.settingService.getOrganizationLogoUrl(res.proposal.organizationId) : '../assets/TinselLogo.png';
      this.showTinselPayCardPassFee = res.showTinselPayCardPassFee;
      // Proposal Settings
      this.acceptProposalTerms = res.proposalSettings.acceptProposalTerms;
      this.businessAttributes = res.proposalSettings.businessAttributes;
      if (this.proposal.agreementType == 'MULTI') {
        (document.getElementById('MULTI_PRICING_DESCRIPTION') as HTMLDivElement).innerHTML = this.fixBreaksInHtml(res.proposalSettings.multiPricingDescription);
        (document.getElementById('MULTI_PRICING_DESCRIPTION') as HTMLDivElement).hidden = false;
      }
      let content =  this.normalizeHtml((' ' + res.proposalSettings.proposalTerm?.content).slice(1).replace(/&nbsp;/g, ' '));
      (document.getElementById('PROPOSAL_TERM') as HTMLDivElement).innerHTML = content;
      this.signaturesAreEnabled = res.proposalSettings.signaturesAreEnabled;
      if(this.signaturesAreEnabled) {
        this.followUpQuestionsForm.get('signedBy').addValidators(Validators.required)
      } else {
        this.followUpQuestionsForm.get('signedBy').removeValidators(Validators.required)
      }
      this.calculateProposalPricing();
      this.getProposalSetting();

      this.proposalLoaded = true;
    });
  }

  // Calculate Proposal Pricing
  private calculateProposalPricing(): void {
    this.proposalService.calculateProposalPricing(this.proposalId).subscribe((res) => {
      this.proposalPricing = res;
    });
  }

  // Multi Option Is Enabled
  multiOptionIsEnabled(key: string): boolean {
    return this.proposal.multiOptions[key]?.isEnabled;
  }

  // Feature Item Has Image
  featureItemHasImage(featureItem: FeatureItem): boolean {
    return featureItem.imageType != 'NONE' && (featureItem.imageType == 'ITEM' && featureItem.itemImageExists);
  }

  // Feature Has At Least One Image
  featureHasAtLeastOneImage(featureId: string): boolean {
    if (this.featureHasAtLeastOneImageMap.has(featureId)) {
      return this.featureHasAtLeastOneImageMap.get(featureId);
    } else {
      const featureItems = this.featureItems.get(featureId);
      const hasAtLeastOneImage = featureItems.reduce((acc, featureItem) => { return acc || featureItem.imageType != 'NONE' && (featureItem.imageType == 'ITEM' && featureItem.itemImageExists); }, false);
      this.featureHasAtLeastOneImageMap.set(featureId, hasAtLeastOneImage);
      return hasAtLeastOneImage;
    }
  }

  // Print
  print(): void {
    setTimeout(() => {
      window.print();
    }, 500);
  }

  // Term Acceptance Changed
  termAcceptanceChanged(): void {
    const termElements = Array.prototype.slice.call(document.getElementsByName('ACCEPT_TERM'));
    this.allTermsAccepted = termElements.reduce((acc: boolean, cValue: HTMLInputElement) => { return acc && cValue.checked; }, true);
  }

  updateFeatureSelected(feature: Feature, event: any): void {
    const checked = (event.target as HTMLInputElement).checked;
    feature.isSelected = checked;
    this.proposalService.updateFeature(this.proposalId, feature.id, event.target.checked, this.proposal.organizationId).subscribe(() => {
      this.calculateProposalPricing();
    });

  }

  // Update Feature Included
  // updateFeatureIncluded(feature: Feature): void {
  //   this.featureService.updateFeatureIncluded(feature.id, feature.isHidden).subscribe(() => {
  //     const index = this.features.findIndex((f) => { return f.id == feature.id });
  //     this.features[index].included = feature.included;
  //     this.calculateProposalPricing();
  //   });
  // }

  // Select Multi Option
  selectMultiOption(option: string): void {
    this.selectedMultiOption = option;
  }

  // Select Proposal
  selectProposal(): void {
    this.allTermsAccepted = false;
    this.openModal(this.followUpQuestionsModal);
  }

  // Can Accept Proposal
  canAcceptProposal(): boolean {
    return !this.allTermsAccepted
      && this.acceptProposalTerms.length > 0;
  }

  // Accept Proposal
  acceptProposal(): void {
    if (this.signaturesAreEnabled && this.isSignatureCanvasBlank()) {
      this.alertService.showWarningAlert('Signature Cannot Be Blank');
    } else {
      this.loading = true;

      const signatureCanvas = document.getElementById('SIGNATURE_CANVAS') as HTMLCanvasElement;
      const signatureBase64 = (this.isSignatureCanvasBlank()) ? null : signatureCanvas.toDataURL('image/png').split(';base64,')[1];
      const acceptedProposal = {
        id: this.proposalId,
        selectedMultiOption: this.selectedMultiOption,
        installationWeek: (this.followUpQuestionsForm.value.installationWeek == 'NONE') ? null : this.followUpQuestionsForm.value.installationWeek,
        removalType: this.followUpQuestionsForm.value.removalType,
        additionalInfo: this.followUpQuestionsForm.value.additionalInfo,
        method: 'View Proposal',
        signature: signatureBase64,
        amount: this.proposal.amount,
        daysUntilDue: this.proposal.daysUntilDue,
        amountType: this.proposal.amountType,
        signedBy: this.followUpQuestionsForm.value.signedBy
      };
      this.proposalService.acceptProposal(acceptedProposal).subscribe(async (res) => {
        this.alertService.showSuccessAlert('Proposal Accepted');
        this.modalReference.close();
        this.addProposalInteraction('ACCEPTED');
        this.printProposal();
        this.resetFollowUpQuestionsForm();
        this.loading = false;

        if (this.proposal.rowNumber == 1 && this.proposal.isInvoiceRequired) {
          this.redirectToInvoice(res.invoiceId);
        }
      });
    }
  }

  private getProposalSetting() {
    this.proposalService.proposalSetting(this.proposal.id).subscribe((res) => {
      this.preferredInstallation = res.preferredInstallation;
      this.preferredRemoval = res.preferredRemoval;
      this.preferredInstallationOptions = res.preferredInstallationOptions;
    });
  }

  redirectToInvoice(invoiceId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/invoices/${invoiceId}/view`]));
    this.router.navigateByUrl(url);
  }

  // Reject Proposal
  rejectProposal(): void {
    this.proposalService.rejectProposal(this.proposalId, this.proposal.projectId, true).subscribe(() => {
      this.alertService.showSuccessAlert('Proposal Rejected');
      this.addProposalInteraction('REJECTED');
      this.printProposal();
    });
  }

  // Add Proposal Interaction
  private addProposalInteraction(type: string): void {
    if (!/proposals\/:proposalId\/view/g.test(this.route.snapshot.routeConfig.path)) return;
    this.proposalService.addProposalInteration({ proposalId: this.proposalId, type: type }).subscribe(() => {
      if (type == 'VIEWED') this.storeViewedInteractionToken();
    });
  }

  // Store Viewed Interaction Token
  private storeViewedInteractionToken(): void {
    sessionStorage.setItem(`${this.proposalId}.interaction.VIEWED`, true.toString());
  }

  // Has Been Viewed
  private hasBeenViewed(): boolean {
    return sessionStorage.getItem(`${this.proposalId}.interaction.VIEWED`) !== null;
  }

  // Reset Follow Up Questions Form
  private resetFollowUpQuestionsForm(): void {
    this.followUpQuestionsForm.reset();
    this.followUpQuestionsForm.controls.installationWeek.setValue('NONE');
    this.followUpQuestionsForm.controls.installationWeek.enable();
    this.followUpQuestionsForm.controls.removalType.setValue('NONE');
    this.followUpQuestionsForm.controls.removalType.enable();
    this.followUpQuestionsForm.controls.additionalInfo.setValue(null);
    this.followUpQuestionsForm.controls.additionalInfo.enable();
  }

  // View Follow Up Questions
  viewFollowUpQuestions(): void {
    this.followUpQuestionsForm.controls.installationWeek.setValue((this.acceptedProposal['installationWeek'] == null) ? 'NONE' : this.acceptedProposal['installationWeek']);
    this.followUpQuestionsForm.controls.installationWeek.disable();
    this.followUpQuestionsForm.controls.removalType.setValue(this.acceptedProposal['removalType']);
    this.followUpQuestionsForm.controls.removalType.disable();
    this.followUpQuestionsForm.controls.additionalInfo.setValue(this.acceptedProposal['additionalInfo']);
    this.followUpQuestionsForm.controls.additionalInfo.disable();
    this.openModal(this.followUpQuestionsModal);
  }

  // Get Signature Image Data URI
  getSignatureImageDataURI(): string {
    return 'data:image/png;base64,' + this.acceptedProposal.signature;
  }

  // Is Signature Canvas Blank
  private isSignatureCanvasBlank(): boolean {
    const signatureCanvas = document.getElementById('SIGNATURE_CANVAS') as HTMLCanvasElement;
    if (signatureCanvas === null) return true;
    const context = signatureCanvas.getContext('2d');
    const pixelBuffer = new Uint32Array(context.getImageData(0, 0, signatureCanvas.width, signatureCanvas.height).data.buffer);
    return !pixelBuffer.some((color) => { return color !== 0; });
  }

  fixBreaksInHtml(html: string): string {
    const NBH = '\u2011'; // non-breaking hyphen (&#8209;)
    const WJ  = '\u2060'; // word joiner (&#8288;)

    const container = document.createElement('div');
    container.innerHTML = html;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();

    while (node) {
      let t = node.nodeValue ?? '';

      // 1) Replace &nbsp; leftovers with normal spaces
      t = t.replace(/\u00A0/g, ' ');

      // 2) Replace hyphen between letters with non-breaking hyphen (1-year, per-year, first-year)
      t = t.replace(/(\p{L})-(?=\p{L})/gu, `$1${NBH}`);

      // 3) Normalize number ranges: 2-5 or 2 – 5
      //    → en dash with word joiners
      t = t
        .replace(/(\d)\s*-\s*(\d)/g, '$1–$2')                // convert hyphen range to en dash
        .replace(/(\d)\s*–\s*(\d)/g, `$1${WJ}–${WJ}$2`);     // keep numbers tight around dash

      node.nodeValue = t;
      node = walker.nextNode();
    }

    return container.innerHTML;
  }

  private normalizeHtml(html: string): string {
    if (!html) return '';
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const ols = wrapper.querySelectorAll('ol');
    let counter = 1;

    ols.forEach((ol) => {
      const lis = Array.from(ol.children).filter(
        (child) => child.tagName === 'LI'
      );
      if (!lis.length) return;

      ol.setAttribute('start', String(counter));
      counter += lis.length;
    });

    return wrapper.innerHTML;
  }

  get visibleFeaturesSorted(): Feature[] {
    const features = (this.features ?? [])
      .filter(f => !f.isHidden);

    // Get only features that have an explicit order
    const orderedFeatures = features
      .filter(f => f.order !== null && f.order !== undefined)
      .sort((a, b) => {
        const orderDiff = a.order! - b.order!;

        if (orderDiff !== 0) {
          return orderDiff;
        }

        return (a.name ?? '').localeCompare(b.name ?? '');
      });

    let orderedIndex = 0;

    // Keep unordered features in their original positions
    return features.map(feature => {
      if (feature.order === null || feature.order === undefined) {
        return feature;
      }

      return orderedFeatures[orderedIndex++];
    });
  }



  // Follow-Up Questions Form Accessors
  get installationWeek() { return this.followUpQuestionsForm.controls.installationWeek; }
  get removalType() { return this.followUpQuestionsForm.controls.removalType; }
  get additionalInfo() { return this.followUpQuestionsForm.controls.additionalInfo; }
}
